<template>
  <div>
    <div class="py-2">
      <section-pdf-viewer :key="pdfReload"
                          :sectionDoc="section"
                          :disableExtend="true"
                          :extended="extended"
                          :maximise="false"
                          :fullscreen="false"
                          :dialog="false"
                          :mode="document.mode"
                          @closeDialog="closeDialog">
        <template #footer-status>
          <div class="mandate-signature-status">
            <template v-if="signaturePending">
              <p v-if="popupBlocked" class="mb-1"><em>Si la page ne s'ouvre pas, votre navigateur bloque peut-être les pop-up. Veuillez les autoriser.</em></p>
            </template>
            <p v-else-if="!canSign" class="mb-1">Le mandat a été signé.</p>
            <p v-if="signError" class="mb-1">{{ signError }}</p>
          </div>
        </template>
      </section-pdf-viewer>
    </div>
  </div>
</template>
<script lang="ts">
  import Section from '@/framework/base/classes/Section';
  import Document from '@/generated/classes/Document';
  import SectionPdfViewer from '@/framework/document/components/SectionPdfViewer.vue';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import Route from '@/generated/classes/Route';
  import { defineComponent, type PropType } from "vue";
  import Button from "@/framework/base/classes/Button.ts";
  import { AppButtons, FwkButtons, RessourceAppButton, RessourceFwkButton } from "@/ressources/globalRessources.ts";
  import PaymentMeans, { Mandat, MandatStatuts } from '@/generated/classes/PaymentMeans';
  import { storeDocument, storePaymentMeans } from '@/generated/stores';
  export default defineComponent({
    components: { SectionPdfViewer },
    props: {
      hideTitle: {
        type: Boolean
      },
      document: {
        type: Object as PropType<Document>,
        required: true
      },
      dialog: {
        type: Boolean
      },
      extended: {
        type: Boolean
      },
      readOnly: {
        type: Boolean
      },
      route: {
        type: Object as PropType<Route>
      }
    },
    data () {
      return {
        signing: false,
        signClicked: false,
        loading: false,
        isSignWindowClosed: undefined as (() => boolean) | undefined,
        signUrl: '',
        popupBlocked: false,
        signError: '',
        pdfReload: 0,
        disposed: false,
        signTimer: undefined as number | undefined
      };
    },
    created(): void {
      window.addEventListener('focus', this.checkSignatureWindow);
      document.addEventListener('visibilitychange', this.checkSignatureWindow);
    },
    beforeUnmount(): void {
      this.disposed = true;
      window.removeEventListener('focus', this.checkSignatureWindow);
      document.removeEventListener('visibilitychange', this.checkSignatureWindow);
      window.clearInterval(this.signTimer);
      this.isSignWindowClosed = undefined;
    },
    computed: {
      paymentMeans (): PaymentMeans {
        if (this.document.objId > 0) {
          return storePaymentMeans.getPaymentMeans(this.document.objId)
        } else {
          return new PaymentMeans();
        }
      },
      mandat (): Mandat {
        if (this.document.objType == ObjTypes.MANDAT_B2B) {
          return this.paymentMeans.mandatB2b;
        } else {
          return this.paymentMeans.mandatB2c;
        }
      },
      signLabel (): string {
        return this.signing
          ? 'Recharger'
          : RessourceAppButton(AppButtons.SIGNER).label;
      },
      signaturePending (): boolean {
        return this.canSign && (this.signClicked || this.mandat.statut == MandatStatuts.SUBMITTED);
      },
      canSign (): boolean {
        return this.mandat.statut < MandatStatuts.SIGNED;
      },
      section (): Section {
        const section = new Section();
        section.objType = ObjTypes.DOCUMENT;
        section.objId = this.document.id;
        section.buttons = this.buttons;
        return section;
      },
      buttons (): Button[] {
        const buttons: Button[] = [];
        if (this.signing && this.canSign) {
          const button = new Button();
          button.show = true;
          button.label = 'En cours de signature';
          button.disabled = true;
          buttons.push(button);
        }
        {
          const button = new Button();
          button.show = this.canSign;
          button.label = this.signLabel; 
          button.callback = this.signing ? this.recharger : this.signer;
          button.disabled = this.loading;
          button.loading = this.loading;
          buttons.push(button);
        }
        if (!this.signing || !this.canSign) {
          const button = new Button();
          button.show = true;
          button.label = RessourceFwkButton(FwkButtons.FERMER).label;
          button.callback = this.FermerDialog;
          button.disabled = false;
          buttons.push(button);
        }
        return buttons;
      },
    },
    watch: {
      canSign(value: boolean): void {
        if (!value) {
          this.signatureCompleted();
        }
      }
    },
    methods: {
      FermerDialog (): Promise<void> {
        return new Promise((resolve) => {
          this.closeDialog();
          resolve();
        });
      },
      async signer(): Promise<void> {
        await this.checkMandatSignature(!this.signing);
      },
      async recharger(): Promise<void> {
        await this.checkMandatSignature(false);
      },
      async checkMandatSignature(openSigningPage: boolean): Promise<void> {
        if (this.loading || this.disposed) return;
        this.loading = true;
        this.signError = '';
        try {
          const url = await PaymentMeans.signMandat(this.document);
          this.signUrl = url?.trim() || '';
          if (this.disposed) return;
          this.signClicked = true;

          const paymentMeans = await PaymentMeans.get(this.document.objId);
          const document = await Document.get(this.document.id);
          if (this.disposed) return;
          storePaymentMeans.mPaymentMeansUpdated(paymentMeans);
          storeDocument.mDocumentUpdated(document);
          this.pdfReload++;
          if (!this.signUrl || !this.canSign) {
            this.popupBlocked = false;
            this.resetSignatureWindowState();
            if (this.canSign) {
              this.signError = 'La signature est confirmée sur Edusign. Le document signé est encore en cours de synchronisation. Veuillez vérifier à nouveau dans quelques instants.';
            }
          } else if (openSigningPage && !this.signing) {
            this.openSignature();
          } else {
            this.signError = 'La signature du mandat n’est pas encore confirmée sur Edusign.';
          }
        } catch {
          this.signError = 'Impossible de vérifier la signature. Veuillez réessayer.';
        } finally {
          this.loading = false;
        }
      },
      openSignature(): void {
        if (!this.signUrl || this.disposed || !this.canSign) return;
        this.signClicked = true;
        sessionStorage.setItem('edusignSigningInProgress', JSON.stringify({
          documentId: this.document.id,
          expiresAt: Date.now() + 30 * 60 * 1000
        }));
        const openedWindow = window.open(encodeURI(this.signUrl), '_blank');
        this.popupBlocked = !openedWindow;
        this.signing = !!openedWindow;
        this.isSignWindowClosed = openedWindow
          ? () => openedWindow.closed
          : undefined;
        window.clearInterval(this.signTimer);
        if (openedWindow) {
          this.signTimer = window.setInterval(() => {
            if (openedWindow.closed) {
              this.signatureReturn();
              return;
            }
          }, 500);
        }
      },
      checkSignatureWindow(): void {
        try {
          if (this.isSignWindowClosed?.()) {
            this.signatureReturn();
          }
        } catch {
          // The raw popup reference is captured outside Vue's reactive state.
        }
      },
      signatureReturn(): void {
        if (!this.signing) return;
        this.resetSignatureWindowState();
      },
      signatureCompleted(): void {
        this.resetSignatureWindowState();
      },
      resetSignatureWindowState(): void {
        this.signing = false;
        window.clearInterval(this.signTimer);
        this.signTimer = undefined;
        this.isSignWindowClosed = undefined;
        this.pdfReload++;
      },
      closeDialog (): void {
        this.$emit('closeDialog');
      }
    }
  })

</script>

<style scoped>
  .mandate-signature-status {
    display: flex;
    min-height: 44px;
    padding: 0.5rem 0.25rem 0.5rem 1rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-style: italic;
    text-align: center;
  }

  .mandate-signature-status p {
    margin-bottom: 0 !important;
  }
</style>
